import React, { useMemo, type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, SectionContent, SectionHeader, SectionTitle } from '@/components/ui'
import { FluxoPagamentoSheet } from '@/features/simulador/components/fluxo-pagamento'
import {
  createGrupoParcelaItem,
  createParcelaItem,
  createValueItem,
  ValoresSimuladosFactory,
  type SectionGroupFactoryItem,
} from '@/features/simulador/components/simulacao-content/valores-simulados-factory'
import { useSimulacaoContentContext } from '@/features/simulador/context/simulacao-content.context'

const ValoresSimuladosWrapper = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('simulacao')
  const { fluxoPagamento } = useSimulacaoContentContext()

  return (
    <React.Fragment>
      <SectionHeader>
        <SectionTitle>{t('pages.simulacao.valores_simulados.titulo')}</SectionTitle>
        <span className="hidden sm:block">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados}>
                <Button variant="outline" className="h-8 bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </SectionHeader>
      <SectionContent className="pb-16 sm:pb-0">
        {children}

        <span className="block sm:hidden">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados ?? []}>
                <Button variant="outline" className="text-primary h-12 w-full bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </SectionContent>
    </React.Fragment>
  )
}

const ValoresSimuladosContentVertical = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  const groups: SectionGroupFactoryItem[] = [
    {
      withBorder: true,
      items: [createValueItem(t('pages.simulacao.valores_simulados.valor_negociado'), simulacao.valorTotalSimulado)],
    },
    {
      withBorder: true,
      items: [
        createValueItem(t('pages.simulacao.valores_simulados.poupanca'), simulacao.valorPoupanca, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.entrada'), simulacao.entrada, 8),
      ],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.mensais'), simulacao.mensais, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.intermediarias'), simulacao.intermediarias, 8),
      ],
    },
    {
      withBorder: true,
      items: [
        createParcelaItem(t('pages.simulacao.valores_simulados.saldo'), simulacao.saldo, 8, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.pos_conclusao'), simulacao.posConclusao, 8),
      ],
    },
  ]

  return <ValoresSimuladosFactory grupos={groups} />
}

const ValoresSimuladosContentSacocVParcelado = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  const groups: SectionGroupFactoryItem[] = [
    {
      withBorder: true,
      items: [createValueItem(t('pages.simulacao.valores_simulados.valor_negociado'), simulacao.valorTotalSimulado)],
    },
    {
      withBorder: true,
      items: [
        createValueItem(t('pages.simulacao.valores_simulados.poupanca'), simulacao.valorPoupanca, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.entrada'), simulacao.entrada, 8),
      ],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.mensais'), simulacao.mensais, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.intermediarias'), simulacao.intermediarias, 8),
      ],
    },
    {
      items: [
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.pos_conclusao'), simulacao.posConclusao, 4, true),
        createGrupoParcelaItem(
          t('pages.simulacao.valores_simulados.intermediarias_pos_conclusao'),
          simulacao.intermediariasPosConclusao,
          8,
        ),
      ],
    },
  ]

  return <ValoresSimuladosFactory grupos={groups} />
}

const ValoresSimuladosContentSacocH = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  const groups: SectionGroupFactoryItem[] = [
    {
      withBorder: true,
      items: [createValueItem(t('pages.simulacao.valores_simulados.valor_negociado'), simulacao.valorTotalSimulado)],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.entrada'), simulacao.entrada, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.mensais'), simulacao.mensais, 8),
      ],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(
          t('pages.simulacao.valores_simulados.intermediarias'),
          simulacao.intermediarias,
          4,
          true,
        ),
      ],
    },
  ]

  return <ValoresSimuladosFactory grupos={groups} />
}

const ValoresSimuladorContentPrice = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  const groups: SectionGroupFactoryItem[] = [
    {
      withBorder: true,
      items: [createValueItem(t('pages.simulacao.valores_simulados.valor_proposto'), simulacao.valorTotalSimulado)],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.entrada'), simulacao.entrada, 4, true),
        createGrupoParcelaItem(t('pages.simulacao.valores_simulados.mensais'), simulacao.mensais, 8),
      ],
    },
    {
      withBorder: true,
      items: [
        createGrupoParcelaItem(
          t('pages.simulacao.valores_simulados.intermediarias'),
          simulacao.intermediarias,
          4,
          true,
        ),
      ],
    },
  ]

  return <ValoresSimuladosFactory grupos={groups} />
}

export const ValoresSimuladosSection = () => {
  const { simulacao } = useSimulacaoContentContext()

  const content = useMemo(() => {
    if (simulacao.tipoDeAmortizacao === 'SacocV' && simulacao.formaDePagamento === 'Parcelado')
      return <ValoresSimuladosContentSacocVParcelado />
    if (simulacao.tipoDeAmortizacao === 'SacocH') return <ValoresSimuladosContentSacocH />
    if (simulacao.tipoDeAmortizacao === 'Price') return <ValoresSimuladorContentPrice />
    return <ValoresSimuladosContentVertical />
  }, [simulacao.formaDePagamento, simulacao.tipoDeAmortizacao])

  return <ValoresSimuladosWrapper>{content}</ValoresSimuladosWrapper>
}
